import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";
import { GALLERY } from "../data";

const Slider = ({ job }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const move = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid={`gallery-slider-${job.id}`}
      className="relative aspect-[3/2] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-2xl border border-white/10"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && move(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
    >
      <img src={job.after} alt={`${job.title} — after`} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={job.before} alt={`${job.title} — before`} draggable={false} className="absolute inset-0 h-full w-full object-cover" />
      </div>

      <div className="pointer-events-none absolute top-0 bottom-0 w-[2px] bg-white/90 shadow-[0_0_16px_rgba(255,176,0,0.8)]" style={{ left: `${pos}%` }}>
        <div className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[#ffb000] text-black shadow-[0_0_24px_rgba(255,176,0,0.6)]">
          <ChevronsLeftRight className="h-5 w-5" strokeWidth={2.5} />
        </div>
      </div>

      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 font-accent text-[10px] uppercase tracking-[0.25em] text-white backdrop-blur-md">Before</span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-[#ffb000]/90 px-3 py-1 font-accent text-[10px] uppercase tracking-[0.25em] text-black backdrop-blur-md">After</span>
    </div>
  );
};

export const Gallery = () => {
  return (
    <section id="proof" data-testid="gallery" className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16 flex flex-col gap-4 md:mb-20 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <span className="font-accent text-xs uppercase tracking-[0.4em] text-[#ffb000]">The Proof</span>
            <h2 className="mt-4 font-display text-4xl font-700 uppercase leading-none chrome-text md:text-6xl">
              Real Jobs. Real Results.
            </h2>
          </div>
          <p className="max-w-sm font-body text-sm leading-relaxed text-neutral-400">
            Drag the slider and see the difference for yourself. Every customer gets photos like these of their own vehicle — nothing hidden, ever.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {GALLERY.map((job, i) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`gallery-card-${job.id}`}
              className="group rounded-3xl border border-white/10 bg-[#080808] p-4 transition-colors duration-500 hover:border-[#ffb000]/50"
            >
              <Slider job={job} />
              <div className="p-4 pt-5">
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-[#ffb000]">{job.vehicle}</div>
                <h3 className="mt-2 font-accent text-xl font-600 uppercase tracking-wide text-white">{job.title}</h3>
                <p className="mt-3 font-body text-sm leading-relaxed text-neutral-400">{job.copy}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
